import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { Box, Divider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { invoiceSave } from "../../../redux/Thunk/InvoiceModule/InvoiceThunk";
import Loader from "../../../Components/Ui_elements/Loader";
import Btn from "../../../Components/Ui_elements/Btn";
import TextFieldComponent from "../../../Components/TextFieldComponent";
import { showSnackbar } from "../../../redux/Slice/snackbar/snackbarSlice";
import SaveIcon from '@mui/icons-material/Save';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { calculateDueDateFreelance, capitalizeName, isValidNumber, localizeNumber, normalizeNumber } from "../../../Helper/generalhelper";

const safeToFixed = (value, digits = 2) => {
    const num = Number(value);
    return (Number.isFinite(num) ? num : 0).toFixed(digits);
};

const ViewInvoiceLinguistCreate = () => {
    const { user } = useSelector((state) => state?.root?.authUser);
    const { freelance_invoice_with_settings } = useSelector((state) => state.root.ReportModule);
    const { loading } = useSelector((state) => state.root.loading);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [error, setError] = useState({});
    // Form state management
    const [formData, setFormData] = useState({
        nokValue: '',
        nokTotal: '',
        document: '',
        documentName: '',
        invoice_number: ``,
    });
    const isNokCurrency = freelance_invoice_with_settings?.data?.[0]?.freelance_second_currency;

    const bankInfoRaw = freelance_invoice_with_settings?.userPaymentData?.vBankInfo;
    const bankInfo = bankInfoRaw ? JSON.parse(bankInfoRaw) : {};

    const addressRaw = freelance_invoice_with_settings?.data?.[0]?.freelanceAddressDetail;
    let parsedAddress = [];
    try {
        parsedAddress = addressRaw ? JSON.parse(addressRaw) : [];
        if (!Array.isArray(parsedAddress)) parsedAddress = [];
    } catch (e) {
        parsedAddress = [];
    }

    const addressMap = Object.fromEntries(parsedAddress.map(item => [item.id, item.value]));

    const streetNumber = addressMap['address1_street_number'] || '';
    const locality1 = addressMap['address1_locality'] || '';
    const state = addressMap['address1_administrative_area_level_1'] || '';
    const country = addressMap['address1_country'] || '';
    const postalCode1 = addressMap['address1_postal_code'] || '';
    const freelanceAddressStreet = freelance_invoice_with_settings?.data?.[0]?.freelanceAddress ?? '';

    const paymentInfoRaw = freelance_invoice_with_settings?.userPaymentData?.vPaymentInfo;
    let freelancerVatNumber = '';
    try {
        const paymentInfo = paymentInfoRaw ? JSON.parse(paymentInfoRaw) : {};
        freelancerVatNumber = paymentInfo?.tax_id || '';
    } catch (error) {
        //console.warn("Invalid vPaymentInfo JSON:", paymentInfoRaw);
    }

    const scoop = freelance_invoice_with_settings?.data || [];
    const [localScoop, setLocalScoop] = useState(scoop);
    useEffect(() => {
        setLocalScoop(scoop);
        if (!(freelance_invoice_with_settings && freelance_invoice_with_settings.data)) {
            window.history.back();
        }
    }, [freelance_invoice_with_settings?.data]);

    const rawDays = freelance_invoice_with_settings?.data?.[0]?.number_of_days;
    const invoiceDays = rawDays === 0 || !rawDays ? 59 : rawDays;

    const dueDate = dayjs().add(invoiceDays, 'day').format('DD.MM.YYYY');
    const dueDateForPayload = dayjs().add(invoiceDays, 'day').format('YYYY-MM-DD');


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear the error for the field when it changes
        setError(prev => ({ ...prev, [name]: '' }));
    };

    // Enhanced calculation logic
    const calculateValues = () => {
        // Calculate scoop subtotal by summing all scoop values
        const scoopSubtotal = localScoop.reduce((acc, item) => {
            const value = parseFloat(item.scoop_value || item.price_per_job || 0);
            return acc + (isNaN(value) ? 0 : value);
        }, 0);

        // Get description value (additional amount)
        const descriptionValue = parseFloat(formData.description_value || 0);

        // Calculate subtotal (scoop + description)
        const subtotal = scoopSubtotal + (isNaN(descriptionValue) ? 0 : descriptionValue);

        // Get tax rate from settings (default to 0 if not available)
        const taxRate = parseFloat(freelance_invoice_with_settings?.data?.[0]?.tax_percentage || 0);

        // Calculate tax amount
        const tax = (subtotal * taxRate) / 100;

        // Calculate total (subtotal + tax)
        const total = subtotal + tax;

        return {
            subtotal: safeToFixed(subtotal),
            tax: safeToFixed(tax),
            total: safeToFixed(total),
            taxRate: safeToFixed(taxRate)
        };
    };

    const { subtotal, tax, total, taxRate } = calculateValues();

    const hasUserEnteredValue = (value) => {
        return value !== null &&
            value !== undefined &&
            String(value).trim() !== '';
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData?.invoice_number?.trim()) {
            newErrors.invoice_number = "Please enter invoice number";
        }
        if (isNokCurrency) {
            if (!hasUserEnteredValue(formData.nokValue)) {
                newErrors.nokValue = "Please enter NOK VAT amount";
            }
            if (!formData?.nokTotal?.trim()) {
                newErrors.nokTotal = "Please enter NOK total amount";
            }
        }

        return newErrors;
    };
    const handleSave = () => {
        const newErrors = validateForm();
        if (Object.keys(newErrors).length > 0) {
            setError(newErrors);
            // Dispatch snackbar for each validation error
            Object.values(newErrors).forEach((message) => {
                dispatch(
                    showSnackbar({
                        message,
                        type: "error",
                    })
                );
            });
            return;
        }

        // Validate bank information
        const missingBankFields = [];
        if (!bankInfo?.bank_name) missingBankFields.push("Bank Name");
        if (!bankInfo?.address) missingBankFields.push("Bank Address");
        if (!bankInfo?.holder_name) missingBankFields.push("Bank Holder");
        if (!bankInfo?.iban) missingBankFields.push("IBAN");
        if (!bankInfo?.bic) missingBankFields.push("SWIFT/BIC");

        if (missingBankFields.length > 0) {
            dispatch(
                showSnackbar({
                    message: `Please fill in the following payment details:\n- ${missingBankFields.join("\n- ")}`,
                    type: "info",
                })
            );
            return;
        }
        const data = {
            invoice_type: "save",
            freelance_id: freelance_invoice_with_settings?.data?.[0]?.freelanceId,
            createdBy: user?.iUserId,
            job_id: JSON.stringify(scoop?.map((item) => ({ id: item.jobId }))) || "[]",
            payment_type: bankInfo?.payment_method,
            invoice_number: freelance_invoice_with_settings?.data?.[0]?.invoiceCount || 0,
            custom_invoice_no: formData?.invoice_number,
            vat: taxRate || "0.00",
            vat2: normalizeNumber(formData.nokValue) || "0.00",
            job_total: subtotal,
            Invoice_cost: total,
            Invoice_cost2: normalizeNumber(formData.nokTotal) || "0.00",
            invoice_date: dayjs().format("YYYY-MM-DD"),
            currency_rate: 1.0732,
            inv_due_date: dueDateForPayload,
            job: localScoop.map(item => ({
                id: item.jobId,
                value: item.price_per_job.toString()
            })),
        };

        const payload = new FormData();
        payload.append('resourceInvoiceFile', formData.document); // actual File object
        payload.append('resourceInvoiceFileName', formData.documentName);

        // Append all other keys (convert objects to string if needed)
        for (const [key, value] of Object.entries(data)) {
            if (typeof value === 'object') {
                payload.append(key, JSON.stringify(value));
            } else {
                payload.append(key, value);
            }
        }

        // 🔥 Now send payload using Axios or dispatch
        dispatch(invoiceSave(Object.fromEntries(payload.entries()))).then((res) => {
            if (res?.payload?.data?.status === 200) {
                navigate(`/viewinvoicelinguist/${res?.payload?.data?.inserted_id}`);
            } else {
                dispatch(
                    showSnackbar({
                        message: res?.payload?.data?.msg || "Invoice exists with the same due date",
                        type: "error",
                    })
                )
            }
        });
    };


    const handleFileChange = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            dispatch(showSnackbar({
                message: "Select only PDF file",
                type: "info",
            }));
            event.target.value = '';
            return;
        }

        // Just store the actual file object in state
        setFormData(prev => ({
            ...prev,
            document: file,              // file object
            documentName: file.name      // file name
        }));
    };


    return (
        <>
            <Grid container justifyContent="space-between" gap={2} mb={2}>
                <Typography variant="h5" ml={2}>Invoice create (Preview)
                </Typography>
                <Btn sx={{ mr: 2 }} startIcon={<KeyboardBackspaceIcon />} onClick={() => window.history.back()}>Back</Btn>
            </Grid>

            {loading ? <Loader /> : (
                <Box sx={{ p: 3, maxWidth: 900, margin: "auto" }} className="whiteBackground">
                    {/* Header */}
                    <Grid container alignItems="center" spacing={2}>
                        <Grid item xs>
                            <Typography variant="body2">
                                {[
                                    capitalizeName(scoop?.[0]?.freelanceName),
                                    freelanceAddressStreet,
                                    postalCode1 && `${postalCode1} ${locality1}`,
                                    country
                                ]
                                    .filter(Boolean)
                                    .join(' | ')}
                            </Typography>

                        </Grid>
                    </Grid>

                    <Grid container spacing={2} mt={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="h6" fontWeight="bold">
                                {freelance_invoice_with_settings?.settingData?.[0]?.company_name || ""}
                            </Typography>
                            <Typography variant="body2">{freelance_invoice_with_settings?.settingData?.[0]?.address1 || ""}</Typography>
                            <Typography variant="body2">{freelance_invoice_with_settings?.settingData?.[0]?.postcode || ""}{freelance_invoice_with_settings?.settingData?.[0]?.city || ""}</Typography>
                            <Typography variant="body2">{freelance_invoice_with_settings?.settingData?.[0]?.country || ""}</Typography>
                            <Typography variant="body2">{freelance_invoice_with_settings?.settingData?.[0]?.vat_number || ""}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} mt={2}>
                        <Grid ml={2} container justifyContent={'space-between'} xs={12} sm={6}>
                            <Typography variant="body2">
                                Invoice Date : {dayjs().format('DD.MM.YYYY')}
                            </Typography>

                            {freelance_invoice_with_settings?.data?.[0]?.companyName && (
                                <Typography variant="body2">
                                    Company Name : {freelance_invoice_with_settings.data[0].companyName}
                                </Typography>
                            )}

                            <Typography variant="body2">
                                Due Date : {dayjs(calculateDueDateFreelance(dueDateForPayload, 30)).format("DD.MM.YYYY")}
                            </Typography>
                        </Grid>

                    </Grid>

                    {/* Invoice Number */}
                    <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2 }}>
                        <Typography variant="h6" fontWeight="bold">
                            Invoice #
                        </Typography>
                        <Grid item xs={4} sm={4} >
                            <TextFieldComponent
                                name="invoice_number"
                                type="text"
                                value={formData.invoice_number}
                                onChange={handleChange}
                                placeholder="# N-000011"
                                isNotMandatory={true}
                                error={error.invoice_number}
                            />
                        </Grid>
                    </Box>

                    {/* Project List */}
                    <TableContainer sx={{ mt: 4 }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                    <TableCell>Project</TableCell>
                                    <TableCell>
                                        Price in {freelance_invoice_with_settings?.data?.[0]?.client_currency?.split(',')[0]?.trim() || "EUR"}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {localScoop?.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{item?.poNumber} | {item?.item_name}</TableCell>
                                        <TableCell>{localizeNumber(safeToFixed(item?.price_per_job))}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Subtotal, Tax, Total */}
                    <TableContainer sx={{ mt: 4 }}>
                        <Table sx={{ backgroundColor: "#f5f5f5" }}>
                            <TableBody>
                                <TableRow>
                                    <TableCell><b>Subtotal excl. TAX</b></TableCell>
                                    <TableCell align="right"><b>{localizeNumber(Number(subtotal || 0).toFixed(2))}</b></TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>{`${taxRate} % TAX`}</TableCell>
                                    <TableCell align="right">{localizeNumber(tax)}</TableCell>
                                </TableRow>
                                {isNokCurrency && (
                                    <TableRow>
                                        <TableCell>{`${taxRate} % TAX (In NOK)`}</TableCell>
                                        <TableCell align="right">
                                            <TextField
                                                name="nokValue"
                                                type="text"
                                                value={formData.nokValue}
                                                onChange={(e) => {
                                                    isValidNumber(e.target.value) &&
                                                        setFormData(prev => ({ ...prev, nokValue: e.target.value }));
                                                    if (error.nokValue) {
                                                        setError(prev => ({ ...prev, nokValue: '' }));
                                                    }
                                                }}
                                                size="small"
                                                placeholder="Value"
                                                sx={{ maxWidth: '150px' }}
                                                isNotMandatory
                                                onlyNumber={true}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )}

                                <TableRow>
                                    <TableCell><b>Total Price</b></TableCell>
                                    <TableCell align="right"><b>{localizeNumber(safeToFixed(total))}</b></TableCell>
                                </TableRow>
                                {isNokCurrency && <TableRow>
                                    <TableCell>{`Total Price (In NOK)`}</TableCell>
                                    <TableCell align="right">
                                        <Grid itme xs={3}>
                                            <TextField
                                                name="nokTotal"
                                                type="text"
                                                value={formData.nokTotal}
                                                onChange={(e) => {
                                                    isValidNumber(e.target.value) &&
                                                        setFormData(prev => ({ ...prev, nokTotal: e.target.value }));
                                                    if (error.nokTotal) {
                                                        setError(prev => ({ ...prev, nokTotal: '' }));
                                                    }
                                                }}
                                                size="small"
                                                placeholder="Value"
                                                sx={{ maxWidth: '150px' }}
                                                error={error.nokTotal}
                                                isNotMandatory
                                                onlyNumber={true}
                                            />
                                        </Grid>
                                    </TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Notes Section */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-line", mt: 1 }}>
                            Payment for this invoice is due by {dayjs(calculateDueDateFreelance(dueDateForPayload, 30)).format("DD.MM.YYYY")}
                        </Typography>

                    </Box>

                    {/* Bank Info */}
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" gutterBottom><strong>Payment Details:</strong></Typography>
                        <Typography variant="body2">Bank Name: {bankInfo?.bank_name || ""}</Typography>
                        <Typography variant="body2">Bank Address: {bankInfo?.address || ""}</Typography>
                        <Typography variant="body2">Bank Holder: {bankInfo?.holder_name || ""}</Typography>
                        <Typography variant="body2">IBAN: {bankInfo?.iban || ""}</Typography>
                        <Typography variant="body2">SWIFT/BIC: {bankInfo?.bic || ""}</Typography>
                    </Box>

                    {/* Footer */}
                    <Divider sx={{ my: 4, borderBottomWidth: 2, borderImage: "linear-gradient(to right, red, blue, green)", borderImageSlice: 1 }} />
                    <Grid container spacing={2} mt={2} justifyContent="space-between">
                        <Grid item xs={4}>
                            <Typography variant="body2" fontWeight="bold">{capitalizeName(user?.vUserName)}</Typography>
                            <Typography variant="body2">{freelanceAddressStreet}</Typography>
                            <Typography variant="body2">{locality1}</Typography>
                            {/* <Typography variant="body2">{state}</Typography> */}
                            <Typography variant="body2">
                                {[postalCode1].filter(Boolean).join(', ')}
                            </Typography>
                            <Typography variant="body2">{country}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography variant="body2">
                                Email: <Link to={`mailto:${freelance_invoice_with_settings?.data?.[0]?.freelanceEmail}`} underline="hover">{freelance_invoice_with_settings?.data?.[0]?.freelanceEmail}</Link>
                            </Typography>
                        </Grid>
                        <Grid item xs={4} textAlign="right">
                            <Typography variant="body2" fontWeight="bold">VAT NUMBER:</Typography>
                            <Typography variant="body2">{freelancerVatNumber}</Typography>
                        </Grid>
                    </Grid>

                    <Typography variant="body2" mt={2}>
                        Upload your invoice ( Only in PDF fromat)
                    </Typography>
                    <Grid>
                        <input type="file" onChange={handleFileChange} />
                    </Grid>

                    {/* Save Button */}
                    <Box sx={{ display: 'flex', spacing: 2, mt: 4, }}>
                        <Btn startIcon={<SaveIcon />} sx={{ mr: 2 }} onClick={handleSave}>Save Invoice</Btn>
                        <Btn variant="outlined" onClick={() => window.history.back()}>Cancel</Btn>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default ViewInvoiceLinguistCreate;